import { fabric } from "fabric";
import { ObjectSuperTypesEnum, ObjectTypesEnum } from "../../../enums";
import { FabricObject } from "../../../types/FabricCustomType";
import { SeatFactory } from "../factories/seat.factory";
import { RowSeatLabelHelper } from "./RowSeatLabelHelper";

export class RoundTableSeatHelper {
  static async rebuildSeats({
    roundTable,
    table,
    tableGroup,
    seatsQuantity,
    seatRadius,
    seatGap,
    setSeatValue,
  }: any) {
    roundTable.set({
      seatsQuantity,
    });

    const prevCenterRoundTable = roundTable.getCenterPoint();
    
    this.removeSeats(roundTable);

    await this.fillTableWithSeats({
      roundTable,
      table,
      seatRadius,
      seatGap,
    });

    const updatedSeats = this.getSeats(roundTable);

    this.applyDistribution({
      roundTable,
      seats: updatedSeats,
      table,
      setSeatValue,
    });

    updatedSeats.forEach((seat: any) => {
      seat.set({
        category: { ...roundTable.category },
      });

      const shape = seat.item(0);

      shape.set({
        fill: `${roundTable.category.color}40`,
        stroke: roundTable.category.color,
      });

      setSeatValue(seat, roundTable, seat.label);
    });
    
    roundTable.addWithUpdate()
    if((updatedSeats.length + roundTable.emptySpaces) % 2 === 0){
      tableGroup.set({
        left: 0,
        top: 0,
      });
    }
    roundTable.setPositionByOrigin(prevCenterRoundTable, "center", "center");
    roundTable.setCoords();
    return updatedSeats;
  }

  static removeSeats(roundTable: any) {
    const seats = roundTable
      .getObjects()
      .filter(
        (obj: any) =>
          obj.superType === ObjectSuperTypesEnum.Seat ||
          obj.superType === ObjectSuperTypesEnum.EmptySpace
      );

    seats.forEach((seat: any) => {
      roundTable.remove(seat);
    });
  }

  static getSeats(roundTable: any) {
    return roundTable
      .getObjects()
      .filter((obj: any) => obj.superType === ObjectSuperTypesEnum.Seat);
  }

  static async fillTableWithSeats({
    roundTable,
    table,
    seatRadius,
    seatGap,
  }: any) {
    const seatsQuantity = roundTable.seatsQuantity ?? 0;
    const emptySpaces = roundTable.emptySpaces ?? 0;

    if (seatsQuantity <= 0) return;

    const totalPositions = seatsQuantity + emptySpaces;

    const tableRadius = table.radius ?? 0;
    const offset = seatRadius + seatGap;
    const radius = tableRadius + offset;

    const seats: any[] = [];

    for (let i = 0; i < totalPositions; i++) {
      const isEmptySpace = i >= seatsQuantity;
      const angle = ((2 * Math.PI) / totalPositions) * i - Math.PI / 2;

      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      if (isEmptySpace) {
        const emptySpace = new fabric.Circle({
          left: x,
          top: y,
          radius: seatRadius,
          originX: 'center',
          originY: 'center',
          fill: 'transparent',
          stroke: 'transparent',
          selectable: false,
          evented: false,
          visible: true,
        }) as FabricObject<fabric.Circle>;

        emptySpace.set({
          superType: ObjectSuperTypesEnum.EmptySpace,
        });

        seats.push(emptySpace);
        continue;
      }

      const seat = await SeatFactory.create({
        left: x,
        top: y,
        rowSeatSize: seatRadius * 2,
        defaultSeatColor: roundTable.category.color,
        tableLabel: roundTable.tableLabeling.label,
        sectionLabel: roundTable.section.label
      });

      seats.push(seat);
    }

    seats.forEach((seat: any) => {
      roundTable.add(seat);
    });
  }

  static applyDistribution({
    roundTable,
    seats,
    table,
    setSeatValue,
  }: any) {
    const orderedSeats = this.getOrderedTableSeats({
      seats,
      table,
    });
    
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem: roundTable,
      seats: orderedSeats,
      distributionKey: roundTable.seatLabeling.key,
      startsAt: roundTable.seatLabeling.startsAt,
      setSeatValue,
      distributionObject: 'seatLabeling'
    });
  }

  static getOrderedTableSeats({ seats, table }: any) {
    if (!seats?.length) return [];

    const tableCenter = this.getObjectCanvasCenter(table);

    return seats
      .map((seat: any) => {
        const center = this.getObjectCanvasCenter(seat);

        const dx = center.x - tableCenter.x;
        const dy = center.y - tableCenter.y;

        let angle = Math.atan2(dy, dx);
        let normalizedAngle = angle - Math.PI;

        if (normalizedAngle < 0) {
          normalizedAngle += Math.PI * 2;
        }

        return {
          seat,
          angle: normalizedAngle,
        };
      })
      .sort((a: any, b: any) => a.angle - b.angle)
      .map((item: any) => item.seat);
  }

  static getObjectCanvasCenter(obj: any) {
    const center = obj.getCenterPoint();

    return {
      x: center.x,
      y: center.y,
    };
  }

  static getRoundTableParts(roundTable: any) {
    const tableGroup = roundTable
      .getObjects(ObjectTypesEnum.Group)
      .find((obj: any) => obj.superType === ObjectSuperTypesEnum.Table);

    const table = tableGroup
      ?.getObjects()
      .find((obj: any) => obj.type === ObjectTypesEnum.Circle);

    return {
      tableGroup,
      table,
    };
  }

  static calculateMaxSeats(seatRadius: number, gap: number, table: fabric.Circle) {
    const seatDiameter = seatRadius * 2;
    const tableRadius = table.radius ?? 0;

    const placementRadius = tableRadius + seatRadius + gap;
    const circumference = 2 * Math.PI * placementRadius;

    return Math.floor(circumference / seatDiameter);
  }

  static calculateMaxEmptySpaces(seatsQuantity: number, maxPositions: number) {
    return Math.max(0, maxPositions - seatsQuantity)
  }

  static getObjectCenter(obj: any) {
    const width = obj.getScaledWidth?.() ?? 0;
    const height = obj.getScaledHeight?.() ?? 0;

    return {
      x: (obj.left ?? 0) + width / 2,
      y: (obj.top ?? 0) + height / 2,
    };
  }
}